import React from 'react';
import { ActivityIcon, ArrowDownIcon } from 'lucide-react';
import { CTAButton } from '../CTAButton';
import { clinicalContextFactors, evidenceSources, routeSteps } from '../../data/content';
export function ProblemSection() {
  return <section className="w-full bg-navy-900 py-14 sm:py-20" data-id="element-309" data-mp="8kjtb9k14i8m">
      <div className="mx-auto w-full max-w-3xl px-5" data-id="element-310" data-mp="dj4t57vdczwj">
        <p className="text-[0.7rem] font-bold uppercase tracking-[0.16em] text-cyan-bright" data-id="element-311" data-mp="dhjzrlksjn09">
          From Clinical Uncertainty to Structured Decision-Making
        </p>
        <h2 className="mt-3 font-display text-[1.7rem] font-extrabold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-312" data-mp="3na7s4zrgqq2">
          Stop Searching Through Multiple Sources. Start Following One Clinical
          Route.
        </h2>

        <div className="mt-7 flex flex-wrap items-center gap-2" data-id="element-313" data-mp="3qs9ifte2p285">
          {evidenceSources.map(source => <span key={source} className="rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 font-display text-sm font-bold text-slate-200" data-id="element-314" data-mp="1oytiu700cd11">
              {source}
            </span>)}
          <span className="text-[0.72rem] font-medium uppercase tracking-[0.1em] text-slate-400" data-id="element-315" data-mp="1inym028rz32f">
            and other clinical evidence sources
          </span>
        </div>

        <div className="mt-8 space-y-5 text-[1rem] leading-relaxed text-slate-300" data-id="element-316" data-mp="1seb4lox14m5x">
          <p data-id="element-317" data-mp="14ogrd68ssvl5">
            Every year, physicians have access to an increasing amount of
            information about erectile dysfunction from multiple guidelines,
            scientific societies, reviews, and clinical resources.
          </p>
          <p className="text-lg font-semibold text-white" data-id="element-318" data-mp="1dr855cp5c3fy">
            The problem isn’t that information is missing.
          </p>
          <p className="text-lg font-semibold text-cyan-bright" data-id="element-319" data-mp="2p4ttivn6m10f">
            The problem is knowing which information matters for THIS patient.
          </p>
          <p data-id="element-320" data-mp="2k4xrh1y1ojni">
            While you are trying to determine whether you should investigate
            cardiovascular risk, metabolic factors, hormonal factors,
            medications, psychological contributors, or other associated
            conditions…
          </p>
          <p className="rounded-2xl border border-alert-500/30 bg-alert-500/10 px-4 py-4 font-semibold text-white" data-id="element-321" data-mp="1qsr98lphotzv">
            …your patient is already waiting for a clinical decision.
          </p>
        </div>

        <div className="mt-10 rounded-3xl border border-white/10 bg-navy-950 p-5 sm:p-7" data-id="element-322" data-mp="z2uubtviw9zt">
          <h3 className="font-display text-xl font-extrabold text-white sm:text-2xl" data-id="element-323" data-mp="132n429wnkm6r">
            And erectile dysfunction is rarely just about the erection.
          </h3>
          <p className="mt-2 text-[0.95rem] text-slate-400" data-id="element-324" data-mp="1csybw7jgjfix">
            It may exist within a broader clinical context involving:
          </p>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2" data-id="element-325" data-mp="fd2j5lgxveuu">
            {clinicalContextFactors.map(factor => <li key={factor} className="flex items-start gap-2.5 rounded-xl bg-white/5 px-3 py-2.5 text-[0.9rem] font-medium text-slate-200" data-id="element-326" data-mp="kb47olx4w6dp">
                <ActivityIcon className="mt-0.5 h-4 w-4 shrink-0 text-cyan-bright" aria-hidden="true" data-id="element-327" data-mp="7v4g9zt9j20l" />
                {factor}
              </li>)}
          </ul>
          <p className="mt-6 text-[0.98rem] leading-relaxed text-slate-300" data-id="element-328" data-mp="3v1dmo6qyg9m5">
            With{' '}
            <span className="font-semibold text-white" data-id="element-329" data-mp="3q1r3lgog3z5f">
              Clinical Route for Erectile Dysfunction™
            </span>
            , this complexity is organized into a logical sequence of evaluation
            and decision points.
          </p>
        </div>

        <ol className="mt-10 space-y-3" data-id="element-330" data-mp="3bcv0ly18xcbo">
          {routeSteps.map((step, index) => <li key={step.number} data-id="element-331" data-mp="378x0pl43jxhk" data-mpi={index}>
              <div className="flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4 sm:p-5" data-id="element-332" data-mp="37b9jg0hoibvo">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-500 font-display text-lg font-extrabold text-white" data-id="element-333" data-mp="167h2y4pwhou2">
                  {step.number}
                </span>
                <div data-id="element-334" data-mp="wi78ii7l3u2s">
                  <h3 className="font-display text-base font-extrabold text-white sm:text-lg" data-id="element-335" data-mp="wgmf4vx0ah6i">
                    {step.title}
                  </h3>
                  <p className="mt-1 text-[0.9rem] leading-relaxed text-slate-400" data-id="element-336" data-mp="mj7yowb31se7">
                    {step.text}
                  </p>
                </div>
              </div>
              {index < routeSteps.length - 1 ? <div className="flex justify-center py-1.5" data-id="element-337" data-mp="djbhffr0vvt6">
                  <ArrowDownIcon className="h-4 w-4 text-brand-500" aria-hidden="true" data-id="element-338" data-mp="ihmpbkxrbw8r" />
                </div> : null}
            </li>)}
        </ol>

        <div className="mx-auto mt-10 max-w-md" data-id="element-339" data-mp="2cvxdrsafwoe0">
          <CTAButton label="I want to make decisions with one Clinical Route" note="Immediate access · One-time payment" data-id="element-340" data-mp="2ctky5ls5mq44" />
        </div>
      </div>
    </section>;
}